import {
  EErrorMessageMode,
  EMinimumAndroidVersion,
  EMobileGenerationType,
  EProjectPublishMethod,
  EProjectType,
  ICommonSettings,
  IConfig,
  IConfigLocalization
} from "@kuika/kuika-cl-model";
import { Guid } from "guid-typescript";
import { ConfigService } from "../../../services/config-service";
import { notifyDesignerLiveToolIdle } from "../../../services/designer-live/designer-live";
import { DesignerPresenceReporter } from "../../../services/designer-live/designer-presence";
import { DesignerSyncService } from "../../../services/designer-live/designer-sync-service";
import { PartialContextProvider } from "../../../shared/patial/partial-context-provider";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { IScreenDesignContextState } from "../screen-design-context";
import { IScreenDesignContextProviderProps, ScreenDesignContextProvider } from "../screen-design-context-provider";

interface IFetchConfigsProps {
  shouldLockConfigList?: boolean;
  shouldLockPublishPopup?: boolean;
  shouldLockPage?: boolean;
  callback?: () => void;
  shouldFetchGlobals?: boolean;
}

export interface ConfigManagerProviderState {
  configLocalizations: IConfigLocalization[];
  setConfigLocalizations: (configLocalizations: IConfigLocalization[]) => void;
  configurations: IConfig[];
  commonSettings: ICommonSettings;
  isConfigPageLoading: boolean;
  isConfigListLoading: boolean;
  isPublishPopupLoading: boolean;
  setConfigPageLoadingStatus: (isConfigPageLoading: boolean) => void;
  fetchConfigs: (options: IFetchConfigsProps) => void;
  fetchLightweightConfigs: (options: IFetchConfigsProps) => void;
  addConfiguration: (configuration: IConfig) => void;
  /** `true` when the update was written, `false` when the user declined the stale-write prompt. */
  editConfiguration: (configuration: IConfig) => Promise<boolean>;
  deleteConfiguration: (configurationId: string) => void;
  duplicateConfiguration: (configuration: IConfig) => void;
  selectedConfigurationId: string;
  activeConfiguration?: IConfig;
  setActiveConfiguration: (config: IConfig) => void;
  getSelectedConfiguration: () => IConfig | undefined;
  cancelCreatingOrEditingConfiguration: () => void;
  setCreateConfigActive: () => void;
  setCreateConfigVisibilityOn: (name: string, globalConfig?: IConfig) => void;
  isCreateConfigActive: boolean;
  activeCreatingConfigType?: EProjectPublishMethod;
  isProjectSettingsActive: boolean;
  setProjectSettingsActive: (isActive: boolean) => void;
}

export class ConfigManagerProvider extends PartialContextProvider<
  ScreenDesignContextProvider,
  IScreenDesignContextProviderProps,
  IScreenDesignContextState,
  ConfigManagerProviderState
> {
  static defaultState: ConfigManagerProviderState = {
    configLocalizations: [],
    setConfigLocalizations: () => {},
    configurations: [],
    commonSettings: {} as ICommonSettings,
    isConfigPageLoading: false,
    isConfigListLoading: false,
    isPublishPopupLoading: false,
    setConfigPageLoadingStatus: () => {},
    fetchConfigs: () => {},
    fetchLightweightConfigs: () => {},
    addConfiguration: () => {},
    editConfiguration: () => Promise.resolve(false),
    deleteConfiguration: () => {},
    duplicateConfiguration: () => {},
    selectedConfigurationId: "",
    activeConfiguration: undefined,
    setActiveConfiguration: () => {},
    getSelectedConfiguration: () => undefined,
    cancelCreatingOrEditingConfiguration: () => {},
    setCreateConfigActive: () => {},
    setCreateConfigVisibilityOn: () => {},
    isCreateConfigActive: false,
    activeCreatingConfigType: undefined,
    isProjectSettingsActive: false,
    setProjectSettingsActive: () => {}
  };

  getProviderValues = (): ConfigManagerProviderState => {
    return {
      configLocalizations: this.state.configLocalizations,
      setConfigLocalizations: this.setConfigLocalizations,
      configurations: this.state.configurations,
      commonSettings: this.state.commonSettings,
      isConfigPageLoading: this.state.isConfigPageLoading,
      isConfigListLoading: this.state.isConfigListLoading,
      isPublishPopupLoading: this.state.isPublishPopupLoading,
      setConfigPageLoadingStatus: this.setConfigPageLoadingStatus,
      fetchConfigs: this.fetchConfigs,
      fetchLightweightConfigs: this.fetchLightweightConfigs,
      addConfiguration: this.addConfiguration,
      editConfiguration: this.editConfiguration,
      deleteConfiguration: this.deleteConfiguration,
      duplicateConfiguration: this.duplicateConfiguration,
      selectedConfigurationId: this.state.selectedConfigurationId,
      activeConfiguration: this.state.activeConfiguration,
      setActiveConfiguration: this.setActiveConfiguration,
      getSelectedConfiguration: this.getSelectedConfiguration,
      cancelCreatingOrEditingConfiguration: this.cancelCreatingOrEditingConfiguration,
      setCreateConfigActive: this.setCreateConfigActive,
      setCreateConfigVisibilityOn: this.setCreateConfigVisibilityOn,
      isCreateConfigActive: this.state.isCreateConfigActive,
      activeCreatingConfigType: this.state.activeCreatingConfigType,
      isProjectSettingsActive: this.state.isProjectSettingsActive,
      setProjectSettingsActive: this.setProjectSettingsActive
    };
  };

  /**
   * Project Settings is a DIALOG over the Config Manager, not a third canvas surface, so
   * opening it no longer closes whatever configuration is being edited — it used to clear
   * `activeConfiguration` and report the presence entity away for exactly that reason.
   */
  setProjectSettingsActive = (isActive: boolean) => {
    this.setState({ isProjectSettingsActive: isActive });
  };

  setConfigLocalizations = (configLocalizations: IConfigLocalization[]) => {
    this.setState({ configLocalizations });
  };

  fetchConfigLocalizations = async () => {
    const configLocalizationsRes = await ConfigService.getConfigurationLocalizations();

    if (configLocalizationsRes.status !== 200) {
      return;
    }

    this.setConfigLocalizations(configLocalizationsRes.data);
  };

  setConfigPageLoadingStatus = (isConfigPageLoading: boolean) => {
    this.setState({ isConfigPageLoading });
  };

  fetchConfigs = async ({
    shouldLockPage,
    shouldLockPublishPopup,
    shouldLockConfigList = true,
    shouldFetchGlobals,
    callback
  }: IFetchConfigsProps) => {
    this.setState({
      isConfigPageLoading: !!shouldLockPage,
      isPublishPopupLoading: !!shouldLockPublishPopup,
      isConfigListLoading: !!shouldLockConfigList
    });
    try {
      const fetchPromises = [
        ConfigService.getAllConfigs(this.partial.projectProvider.state.project.id),
        shouldFetchGlobals
          ? ConfigService.getGlobalConfigs()
          : Promise.resolve({
              data: {
                commonSettings: {} as ICommonSettings,
                configurations: []
              }
            })
      ];
      let configurations: IConfig[] = [];
      let commonSettings: ICommonSettings;

      const responses = await Promise.allSettled(fetchPromises);
      if (responses[0].status === "rejected") throw responses[0].reason;
      if (responses[1].status === "rejected") throw responses[1].reason;
      configurations = responses[0].value.data.configurations;
      commonSettings = responses[0].value.data.commonSettings;

      if (shouldFetchGlobals) {
        const globalCommonSettingsToMapWithAppCommonSettings = [
          "passwordPolicies",
          "smtpSettings",
          "googleMapsSettings",
          "awsSettings",
          "stripeSettings",
          "iyzicoSettings",
          "genericAuthSettings",
          "oAuth2Settings",
          "ldapSettings",
          "googleAuth",
          "facebookAuth",
          "appStoreConnectApiSettings",
          "googlePlayApiSettings",
          "firebaseSettings",
          "pushNotificationSettings",
          "inversCloudBoxxSettings",
          "appleSignInSettings",
          "sessionSettings",
          "dbConnectionSettings",
          "workflowEngineBaseUrlSettings",
          "msalSettings",
          "otpSettings",
          "searchServiceSettings"
        ];
        responses[1].value.data.configurations.forEach((config) => {
          configurations.push({ ...config, isGlobalConfigOnProjectConfigList: true });
        });

        const globalCommonSettings = responses[1].value.data.commonSettings;

        for (const setting in commonSettings) {
          if (!globalCommonSettings[setting] && !globalCommonSettingsToMapWithAppCommonSettings.includes(setting))
            continue;

          commonSettings[setting] = [
            ...(commonSettings[setting] || []),
            ...(globalCommonSettings[setting]?.map((x) => ({ ...x, isGlobalCommonConfigOnProjectConfig: true })) || [])
          ];
        }
      }
      this.setState({ configurations, commonSettings }, () => {
        if (callback) callback();
      });
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({
        isConfigPageLoading: shouldLockPage ? false : this.state.isConfigPageLoading,
        isConfigListLoading: shouldLockConfigList ? false : this.state.isConfigListLoading,
        isPublishPopupLoading: shouldLockPublishPopup ? false : this.state.isPublishPopupLoading
      });
    }
  };

  fetchLightweightConfigs = async ({ shouldLockPublishPopup, callback }: IFetchConfigsProps) => {
    this.setState({
      isPublishPopupLoading: !!shouldLockPublishPopup
    });

    try {
      const response = await ConfigService.getLightweightConfigs(this.partial.projectProvider.state.project.id);
      const configurations: IConfig[] = response.data.configurations;

      this.setState({ configurations }, () => {
        callback?.();
      });
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({
        isPublishPopupLoading: false
      });
    }
  };

  addConfiguration = async (configuration: IConfig) => {
    try {
      this.setState({ isConfigPageLoading: true });
      await ConfigService.createConfig(this.partial.projectProvider.state.project.id, configuration);
      KMainFunctions.displaySuccessNotification(`[${configuration.name}] has been created successfully`);
      this.fetchConfigs({ shouldLockPage: false, shouldFetchGlobals: true });
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({ isConfigPageLoading: false });
    }
  };

  editConfiguration = async (configuration: IConfig): Promise<boolean> => {
    const proceed: boolean = await DesignerSyncService.checkStaleWrite(
      "config.changed",
      configuration?.id,
      configuration?.name ?? "Configuration"
    );
    // Cancel on that prompt means "go back and refresh first" — so refresh, rather than
    // closing the caller's dialog on a write that never happened and leaving the stale row.
    // It resolves `false` rather than normally, so a caller with a confirm does not report a
    // write that did not happen as saved.
    if (!proceed) {
      this.fetchConfigs({ shouldLockPage: false, shouldFetchGlobals: true });
      return false;
    }
    try {
      this.setState({ isConfigPageLoading: true });
      await ConfigService.updateConfig(this.partial.projectProvider.state.project.id, configuration);
      // No "has been updated" toast: the caller — the rename sheet — holds its confirm's ✓, closes,
      // and the list lights the row under its new name.
      this.fetchConfigs({ shouldLockPage: false, shouldFetchGlobals: true });
      return true;
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
      // This had no callers until the list's Rename row action started using it, so
      // nothing depended on a failed edit resolving quietly. Rethrowing lets that caller
      // tell an update that landed from one that did not — its dialog stays open to retry.
      throw error;
    } finally {
      this.setState({ isConfigPageLoading: false });
    }
  };

  deleteConfiguration = async (configurationId: string) => {
    this.setState({ isConfigPageLoading: true });

    try {
      await ConfigService.deleteConfig(this.partial.projectProvider.state.project.id, configurationId);

      this.setState({
        activeConfiguration:
          configurationId === this.state.activeConfiguration?.id ? undefined : this.state.activeConfiguration,
        isConfigPageLoading: true
      });

      KMainFunctions.displaySuccessNotification("Configuration has been deleted successfully");
      this.fetchConfigs({ shouldLockPage: false, shouldFetchGlobals: true });
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    }
  };

  duplicateConfiguration = async (configuration: IConfig) => {
    try {
      this.setState({ isConfigPageLoading: true });
      await ConfigService.duplicateConfig(this.partial.projectProvider.state.project.id, configuration.id);
      KMainFunctions.displaySuccessNotification(`[${configuration.name}] has been duplicated successfully`);
      this.fetchConfigs({ shouldLockPage: false, shouldFetchGlobals: true });
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({ isConfigPageLoading: false });
    }
  };

  setActiveConfiguration = (config: IConfig) => {
    this.setState({ activeConfiguration: config, isProjectSettingsActive: false });
    DesignerPresenceReporter.reportEntity("config", config?.id, config?.name, "config.changed");
  };

  getSelectedConfiguration = (): IConfig | undefined => {
    return this.state.configurations.find((config) => config.isSelected);
  };

  setCreateConfigActive = () => {
    this.setState({ isCreateConfigActive: !this.state.isCreateConfigActive });
  };

  setCreateConfigVisibilityOn = (name: string, selectedGlobalConfig?: IConfig) => {
    const projectName = this.partial.projectProvider.state.project.name;
    const project = this.partial.projectProvider.state.project;

    // Domain and project names may contain hyphens or underscores, but the two stores disagree on which are legal:
    // Android needs a valid Java package name (underscores, never hyphens), iOS needs hyphens (never underscores).
    const bundleIdBase: string = `com.${this.props.navigator.getCurrentDomain()}.${projectName}`;
    const androidAppBundleId: string = bundleIdBase.replace(/-/g, "_");
    const iosAppBundleId: string = bundleIdBase.replace(/_/g, "-");

    this.setState({
      isCreateConfigActive: true,
      isProjectSettingsActive: false,
      activeConfiguration: {
        id: Guid.create().toString(),
        globalConfigurationId: selectedGlobalConfig?.id,
        name: name ?? "",
        supportWeb: true,
        supportAndroid: false,
        supportIOS: false,
        isSelected: false,
        isRunConfig: false,
        configurationVersion: 1,
        publishMethods: null,
        androidGenerationType: EMobileGenerationType.Native,
        iosGenerationType: EMobileGenerationType.Native,
        apiConfig: {
          dbConnectionStringSettingsId: null,
          workflowDbConnectionStringSettingsId: null,
          workflowEngineBaseUrlSettingsId: null,
          workflowAwsSettingsId: null,
          workflowSmtpSettingsId: null,
          authProviderType: null,
          authProviderSettingsId: null,
          passwordPoliciesId: null,
          googleAuthId: null,
          facebookAuthId: null,
          awsSettingsId: null,
          smtpSettingsId: null,
          stripeSettingsId: null,
          iyzicoSettingsId: null,
          paramPaymentSettingsId: null,
          inversCloudBoxxSettingsId: null,
          googleMapsSettingsId: null,
          isTwoFactorAuthenticationEnabled: false,
          errorMessageMode: EErrorMessageMode.Detailed,
          msalAuthId: null,
          notifyActionsDefaultDurationSettingsId: null,
          searchServiceSettingsId: null,
          searchEngineSettingsId: null,
          fileUploadPathSettingsId: null,
          ...selectedGlobalConfig?.apiConfig,
          virtualDirectory: null,
          backendUrlSettingsId: null,
          commonMobileSettingsId: null,
          openAISettingsId: null,
          azureOpenAISettingsId: null,
          geminiSettingsId: null,
          cacheSettingsId: null,
          bucketUploadSettingsId: null,
          restApiConnectionSettingsId: null,
          ipRateLimitingSettingsId: null,
          documentManagementSettingsId: null
        },
        webConfig: {
          sessionSettingsId: null,
          googleMapsSettingsId: null,
          ...selectedGlobalConfig?.webConfig,
          serverBaseUrl: "",
          appIcon: null,
          title: project.name,
          frontendUrlSettingsId: null,
          dateFormat: null,
          timeFormat: null
        },
        androidConfig:
          project.projectType === EProjectType.Mobile
            ? {
                firebaseSettingsId: null,
                googleMapsSettingsId: null,
                googlePlayApiSettingsId: null,
                ...selectedGlobalConfig?.androidConfig,
                serverUrl: "",
                appName: projectName,
                appBundleId: androidAppBundleId,
                iconFilePath: "",
                iconRoundedFileName: "",
                splashScreenImageName: "",
                splashScreenBackcolor: "E1E5EB",
                targetAndroidVersion: "",
                appVersion: "1.0.0",
                versionCode: 1,
                minimumAndroidVersion: EMinimumAndroidVersion.Android9,
                useClearTextTraffic: true,
                trustAllCerts: true,
                cameraPermissionLabelSettingsId: null,
                locationPermissionLabelSettingsId: null,
                notificationPermissionLabelSettingsId: null,
                bluetoothPermissionLabelSettingsId: null,
                gpsTrackingPermissionLabelSettingsId: null,
                gpsTrackingBackgroundPermissionLabelSettingsId: null,
                gpsTrackingSettingsId: null,
                deviceType: 0,
                orientation: "any",
                imageFit: "default",
                isHapticEnabled: true,
                isSecureScreenModeEnabled: false
              }
            : null,
        iosConfig:
          project.projectType === EProjectType.Mobile
            ? {
                appStoreConnectApiSettingsId: null,
                appleSigninSettingsId: null,
                pushNotificationSettingsId: null,
                deviceType: 0,
                orientation: "any",
                imageFit: "default",
                ...selectedGlobalConfig?.iosConfig,
                serverUrl: "",
                appName: projectName,
                appBundleId: iosAppBundleId,
                iconFileName: "",
                splashScreenImageName: "",
                splashScreenBackcolor: "E1E5EB",
                appVersion: "1.0.0",
                cameraPermissionLabelSettingsId: null,
                locationPermissionLabelSettingsId: null,
                notificationPermissionLabelSettingsId: null,
                bluetoothPermissionLabelSettingsId: null,
                gpsTrackingBackgroundPermissionLabelSettingsId: null,
                gpsTrackingPermissionLabelSettingsId: null,
                gpsTrackingSettingsId: null,
                isHapticEnabled: true
              }
            : null
      }
    });
  };

  cancelCreatingOrEditingConfiguration = () => {
    DesignerPresenceReporter.reportEntity("config", undefined, undefined);
    this.setState(
      {
        activeConfiguration: undefined,
        isCreateConfigActive: false,
        isProjectSettingsActive: false
      },
      () => notifyDesignerLiveToolIdle()
    );
  };
}
